import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  SecondaryButton,
  TabsComponent as Tabs,
  Text,
} from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { DemandMethodRadio } from '~/pages/BalanceImputationPolicy/components/DemandMethodRadio'
import { GenerationMethodOrderList } from '~/pages/BalanceImputationPolicy/components/GenerationMethodOrderList'
import {
  DEFAULT_DEMAND_METHOD,
  DEFAULT_GENERATION_METHOD_ORDER,
} from '~/pages/BalanceImputationPolicy/constants/defaults'
import {
  type ImputationTarget,
  useImputationPolicy,
} from '~/pages/BalanceImputationPolicy/hooks/useImputationPolicy'

type TabKey = ImputationTarget

const DEFAULT_TAB: TabKey = 'demand'

/**
 * 欠損補完設定 (docs/specs/demand/missing-data-interpolation.md)。
 * design/balance/imputation-policy.html に対応し、需要・発電の 2 タブで
 * 組織全体の補完方式を選ぶ。いずれも即時保存 (保存ボタンなし) で、 保存できたことは
 * DS の toast で知らせる (hook 側)。
 */
export const BalanceImputationPolicyPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [tab, setTab] = useState<TabKey>(DEFAULT_TAB)
  const {
    policy,
    isLoading,
    isSaving,
    error,
    resetToken,
    updateDemandMethod,
    moveGenerationMethodUp,
    moveGenerationMethodDown,
    resetToDefault,
    // 「押しても何も変わらない (= 既に既定値)」 の判定は hook が持つ。
    // 既定値の定数を画面側で読み直すと、 送信内容と活性判定が別々に育つため
    // (2026-09-01 codex review 指摘)。
    isAlreadyDefault,
  } = useImputationPolicy()

  const tabs = useMemo<Array<{ key: TabKey; label: string }>>(
    () => [
      { key: 'demand', label: t('page:balanceImputationPolicy.tabs.demand') },
      {
        key: 'generation',
        label: t('page:balanceImputationPolicy.tabs.generation'),
      },
    ],
    [t],
  )

  // 未取得時も spec の既定値で描画する (AC-002 / AC-008)。backend も行が無い
  // 場合は同じ既定値を返すので、初回表示のちらつきが起きない。
  const demandMethod = policy?.demandMethod ?? DEFAULT_DEMAND_METHOD
  const generationOrder =
    policy?.generationMethodOrder != null &&
    policy.generationMethodOrder.length > 0
      ? policy.generationMethodOrder
      : DEFAULT_GENERATION_METHOD_ORDER

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('common:nav.balance') },
        { label: t('common:route.balanceImputationPolicy'), bold: true },
      ]}
      notices={
        error != null ? (
          <CompactAlert
            status="error"
            title={t('page:balanceImputationPolicy.loadError')}
          />
        ) : undefined
      }
    >
      <PageBody>
        <PageIntro description={t('page:balanceImputationPolicy.pageHint')} />
        {/* 取得の失敗は画面の上部 (`PageLayout` の `notices`) に Compact Alert で出す
            (UI 辞書 components/alert-placement.md)。2 タブの両方に関わるのでタブより上。
            保存の失敗は操作の結果なので toast (hook 側) */}
        {/* **`size` を渡さない (DS 既定 = md)。** `TabsProps.size` の型は 'lg' を
            許すが DS の `tabs.recipe.ts` は sm / md しか定義しておらず、'lg' を渡すと
            `createSystem` が merge する **chakra 既定の tabs recipe の lg**
            (`py: '2'` = 8px / `px: '4.5'` = 18px) にフォールスルーする。

            variant は base より後に merge されるので DS base の `padding: '4px 0'` を
            上書きするが、DS base の `&[data-selected] { paddingBottom: 3px }` は
            属性セレクタで specificity が 1 段高く生き残る。結果 **選択タブだけ
            padding-bottom が 3px、未選択は 8px** の非対称になり、trigger が
            `align-items: center` で同じ高さに揃うぶん**選択ラベルが 2px 下がる**
            = 未選択ラベルが下線から余計に離れて見える (2026-09-02 デザイナー指摘、
            playwright 実測で 未選択 8px / 選択 6px を確認。size を外すと両方 4px)。
            併せて下線がラベル幅から左右 18px はみ出す・タブ間隔 24→60px・
            タブバー高 43→51px も size='lg' 由来だった。

            `TabsComponent` を使う他 6 画面はいずれも `size` を渡していない。

            `tabSx` の fontSize は他画面と同じ共有定数を使う (DS 0.14.2 では
            recipe 側が `fontSize: 'lg'` を持つため実効なしだが、撤去は
            `constants/tabStyle.ts` の記述どおり全画面一斉に行う)。 */}
        <Tabs
          items={tabs}
          onSelect={setTab}
          selected={tab}
          tabSx={{ fontSize: tabTriggerFontSize.raw }}
        />
        <RegionLoading
          flexDirection="column"
          flex="1"
          display="flex"
          minHeight={isLoading ? '200px' : 0}
          isLoading={isLoading}
        >
          <PageScrollArea>
            {/* 取得完了まで操作 UI を出さない。backend は update_mask に関わらず
              payload の全 field を書くため、未取得のまま保存すると未編集 field を
              既定値で上書きして保存済み設定を失う。 */}
            <Box
              flexDirection="column"
              gap={4}
              display={policy != null ? 'flex' : 'none'}
              padding={4}
            >
              {tab === 'demand' && (
                <>
                  {/* design `.ipe-tab-hint` = font-size md。 [design 差異] 色は
                    design の mid-emphasis でなく **highEmphasis** (= ライトで黒の
                    本文色) にする (2026-09-02 デザイナー判断。 タブごとに前提が変わる
                    (需要 = 組織全体で 1 方式 / 発電 = 上から順に試す) ので、
                    グレーの補足文だと読み飛ばされるため)。 design (8080) 側も追随済み。

                    accentPrimary は**使わない**。 このアプリではリンク色
                    (`.ecl-text-link` 相当) なので、 押せない文が押せそうに見える。 */}
                  <Text color="Text.highEmphasis" fontSize="md">
                    {t('page:balanceImputationPolicy.demand.hint')}
                  </Text>
                  <DemandMethodRadio
                    disabled={isSaving}
                    onChange={updateDemandMethod}
                    resetToken={resetToken}
                    value={demandMethod}
                  />
                </>
              )}
              {tab === 'generation' && (
                <>
                  {/* 需要タブと同じ `.ipe-tab-hint` 相当 (上のコメント参照)。
                    2 タブで見た目を分けない。 */}
                  <Text color="Text.highEmphasis" fontSize="md">
                    {t('page:balanceImputationPolicy.generation.hint')}
                  </Text>
                  <GenerationMethodOrderList
                    disabled={isSaving}
                    onMoveDown={moveGenerationMethodDown}
                    onMoveUp={moveGenerationMethodUp}
                    value={generationOrder}
                  />
                </>
              )}

              {/* design の「リセット」。 表示中のタブの設定だけを既定値へ戻す
                (注記もタブ別に出し分けて、 操作範囲と文言を一致させる)。
                ラベルは spec conventions「既定値・システム値への上書き = リセット」
                (2026-10-01 PdM 決定 #9335)。 */}
              <Box
                alignItems="center"
                gap={3}
                display="flex"
                maxWidth="720px"
                paddingTop={3}
                borderTopWidth="1px"
                borderTopColor="Border.lowEmphasis"
              >
                <SecondaryButton
                  disabled={isSaving || isAlreadyDefault[tab]}
                  onClick={() => resetToDefault(tab)}
                  size="sm"
                >
                  {t('page:balanceImputationPolicy.reset.button')}
                </SecondaryButton>
                <Text color="Text.lowEmphasis" fontSize="sm">
                  {t(`page:balanceImputationPolicy.reset.note.${tab}`)}
                </Text>
              </Box>
            </Box>
          </PageScrollArea>
        </RegionLoading>
      </PageBody>
    </PageLayout>
  )
}
